import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import { ConsentProvider } from './context/ConsentContext';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import CookieBanner, { CookieSettingsButton } from './components/CookieBanner';
import TrackingScripts from './components/TrackingScripts';

// Eager load critical pages
import Home from './pages/Home';
import EventDetail from './pages/EventDetail';

// Lazy load less critical pages
const SelectSeats = lazy(() => import('./pages/SelectSeats'));
const CheckoutPay = lazy(() => import('./pages/CheckoutPay'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const ForOrganizers = lazy(() => import('./pages/ForOrganizers'));
const TourPage = lazy(() => import('./pages/TourPage'));
const PromoterPage = lazy(() => import('./pages/PromoterPage'));
const OrganizerOnboard = lazy(() => import('./pages/OrganizerOnboard'));
const CompleteOnboarding = lazy(() => import('./pages/CompleteOnboarding'));
const OrganizerDashboard = lazy(() => import('./pages/OrganizerDashboard'));
const CreateEvent = lazy(() => import('./pages/CreateEvent'));
const TerminalSetup = lazy(() => import('./pages/TerminalSetup'));
const DoorSale = lazy(() => import('./pages/DoorSale'));
const OrganizerSettings = lazy(() => import('./pages/OrganizerSettings'));
const EventAnalytics = lazy(() => import('./pages/EventAnalytics'));
const Login = lazy(() => import('./pages/Login'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Terms = lazy(() => import('./pages/Terms'));

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    </div>
  );
}

// Route guard for organizer pages - redirects to login if not authenticated
function ProtectedRoute({ children }) {
  const { isOrganizer } = useApp();
  if (!isOrganizer) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <ConsentProvider>
        <AppProvider>
          <TrackingScripts />
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/for-organizers" element={<ForOrganizers />} />
                <Route path="/tour/:slug" element={<TourPage />} />
                <Route path="/p/:slug" element={<PromoterPage />} />
                <Route path="/event/:id" element={<EventDetail />} />
                <Route path="/event/:id/seats" element={<SelectSeats />} />
                <Route path="/checkout/pay" element={<CheckoutPay />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/login" element={<Login />} />
                <Route path="/organizer/onboard" element={<OrganizerOnboard />} />
                <Route path="/organizer/complete-onboarding" element={<CompleteOnboarding />} />
                <Route path="/organizer/dashboard" element={<ProtectedRoute><OrganizerDashboard /></ProtectedRoute>} />
                <Route path="/organizer/create-event" element={<ProtectedRoute><CreateEvent /></ProtectedRoute>} />
                <Route path="/organizer/terminal" element={<ProtectedRoute><TerminalSetup /></ProtectedRoute>} />
                <Route path="/organizer/door-sale/:eventId" element={<ProtectedRoute><DoorSale /></ProtectedRoute>} />
                <Route path="/organizer/settings" element={<ProtectedRoute><OrganizerSettings /></ProtectedRoute>} />
                <Route path="/organizer/event/:eventId/analytics" element={<ProtectedRoute><EventAnalytics /></ProtectedRoute>} />
                <Route path="/terms" element={<Terms />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Layout>
          <CookieBanner />
          <CookieSettingsButton />
        </AppProvider>
      </ConsentProvider>
    </ErrorBoundary>
  );
}

export default App;
